import { isRequiredTaskStatus, isSignedRequiredOnboardingTask } from 'src/utils/client-roadmap';

import type { TaskStatus, TaskTypeName } from '@savvy/constants/generated/gql-enums';

export type ClientOnboardingActionTask = {
  readonly type: TaskTypeName | null | undefined;
  readonly status: TaskStatus | null | undefined;
  readonly onboardingTask: {
    readonly coClientDocusignSignedAt: string | null | undefined;
    readonly coClientIaaSignedAt: string | null | undefined;
    readonly primaryClientDocusignSignedAt: string | null | undefined;
    readonly primaryClientIaaSignedAt: string | null | undefined;
  } | null;
  readonly user: {
    readonly isPrimaryClient: boolean;
  };
};

function isPrerequisiteTaskType(type: TaskTypeName | null | undefined) {
  return type === 'initial_intake_form' || type === 'account_setup';
}

function isOutstandingTaskStatus(status: TaskStatus | null | undefined) {
  return status === 'todo' || status === 'in_progress';
}

type OnboardingActionsAssessment = {
  readonly hasOnboardingRequirement: boolean;
  readonly complete: boolean;
};

function assessOnboardingActions(
  tasks: readonly ClientOnboardingActionTask[] | null | undefined,
): OnboardingActionsAssessment {
  if (tasks === null || tasks === undefined) {
    return { hasOnboardingRequirement: false, complete: false };
  }
  const required = tasks.filter((task) => isRequiredTaskStatus({ status: task.status }));
  const onboardingTasks = required.filter((task) => task.type === 'onboarding_flow');

  if (onboardingTasks.length === 0) {
    return { hasOnboardingRequirement: false, complete: false };
  }
  const signed = onboardingTasks.every((task) =>
    isSignedRequiredOnboardingTask({
      docusignSignedAt: task.user.isPrimaryClient
        ? task.onboardingTask?.primaryClientDocusignSignedAt
        : task.onboardingTask?.coClientDocusignSignedAt,
      iaaSignedAt: task.user.isPrimaryClient
        ? task.onboardingTask?.primaryClientIaaSignedAt
        : task.onboardingTask?.coClientIaaSignedAt,
      status: task.status,
    }),
  );
  const prerequisitesOutstanding = required.some(
    (task) => isPrerequisiteTaskType(task.type) && isOutstandingTaskStatus(task.status),
  );

  return { hasOnboardingRequirement: true, complete: signed && !prerequisitesOutstanding };
}

export function clientOnboardingActionsComplete(tasks: readonly ClientOnboardingActionTask[] | null | undefined) {
  return assessOnboardingActions(tasks).complete;
}

export function hasIncompleteOnboardingSignature(
  tasks: readonly ClientOnboardingActionTask[] | null | undefined,
): boolean {
  const assessment = assessOnboardingActions(tasks);
  return assessment.hasOnboardingRequirement && !assessment.complete;
}
